import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, Plane, Building2, Briefcase, FileText, Receipt, LogOut, Users, Calendar, Settings, BarChart3, ChevronDown, ChevronRight, History, AlertCircle, BookOpen, Mail, PlaneTakeoff, Bell, MessageSquare, Shield, Database, DollarSign } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [logoUrl, setLogoUrl] = useState("https://customer-assets.emergentagent.com/job_rolebased-invoice/artifacts/q1mu9arn_chat_edit_image_20251029_170030.png");
  const [accountingOpen, setAccountingOpen] = useState(false);
  const [attSettingsOpen, setAttSettingsOpen] = useState(false);
  const [appSettingsOpen, setAppSettingsOpen] = useState(false);
  const [emailNotificationOpen, setEmailNotificationOpen] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);

  useEffect(() => {
    fetchLogo();
    // Load user permissions from localStorage
    const perms = localStorage.getItem('userPermissions');
    if (perms) {
      try {
        setUserPermissions(JSON.parse(perms));
      } catch (e) {
        console.error('Failed to parse permissions:', e);
      }
    }
    // Fetch agency info if user is agency
    if (user?.role === 'agency' && user?.agency_id) {
      fetchAgencyInfo(user.agency_id);
    }
    // Auto-open accounting menu if on invoices, pay-invoice, agency-statements, statistics, or services page
    if (location.pathname === '/invoices' || location.pathname === '/pay-invoice' || location.pathname === '/agency-statements' || location.pathname === '/statistics' || location.pathname === '/services') {
      setAccountingOpen(true);
    }
    // Auto-open ATT Settings menu
    if (location.pathname === '/settings' || location.pathname === '/agents' || 
        location.pathname === '/admin-users' || location.pathname === '/permissions') {
      setAttSettingsOpen(true);
    }
    // Auto-open Email and Notification menu
    if (location.pathname === '/email-automation' || location.pathname === '/email-templates' || 
        location.pathname === '/popups-notices' || location.pathname === '/email-logs') {
      setEmailNotificationOpen(true);
    }
    // Auto-open APP Settings menu
    if (location.pathname === '/backups' || location.pathname === '/airlines-data' || location.pathname === '/airports' || 
        location.pathname === '/user-manual' || location.pathname === '/login-history') {
      setAppSettingsOpen(true);
    }
  }, [location.pathname, user]);

  const fetchLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/company-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.logo_url) {
        setLogoUrl(`${BACKEND_URL}${response.data.logo_url}`);
      }
    } catch (error) {
      console.error("Failed to fetch logo");
    }
  };

  
  const fetchAgencyInfo = async (agencyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agency = response.data.find(a => a.id === agencyId);
      if (agency) {
        setAgencyInfo(agency);
      }
    } catch (error) {
      console.error("Failed to fetch agency info");
    }
  };

  // Helper function to check if user has permission to view a section
  const hasPermission = (section) => {
    // Admin always has access
    if (user?.role === 'admin') return true;
    
    // If no permissions loaded yet, deny access by default
    if (!userPermissions) return false;
    
    // Check permission based on section
    const permissionKey = `${section}_view`;
    return userPermissions[permissionKey] === true;
  };

  // Toggle functions with accordion behavior (only one section open at a time)
  const toggleAccountingSection = () => {
    if (!accountingOpen) {
      setAttSettingsOpen(false);
      setEmailNotificationOpen(false);
      setAppSettingsOpen(false);
    }
    setAccountingOpen(!accountingOpen);
  };

  const toggleAttSettingsSection = () => {
    if (!attSettingsOpen) {
      setAccountingOpen(false);
      setEmailNotificationOpen(false);
      setAppSettingsOpen(false);
    }
    setAttSettingsOpen(!attSettingsOpen);
  };

  const toggleEmailNotificationSection = () => {
    if (!emailNotificationOpen) {
      setAccountingOpen(false);
      setAttSettingsOpen(false);
      setAppSettingsOpen(false);
    }
    setEmailNotificationOpen(!emailNotificationOpen);
  };

  const toggleAppSettingsSection = () => {
    if (!appSettingsOpen) {
      setAccountingOpen(false);
      setAttSettingsOpen(false);
      setEmailNotificationOpen(false);
    }
    setAppSettingsOpen(!appSettingsOpen);
  };

  // Define all menu items with role restrictions
  const allNavItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard", roles: ["admin", "operations", "accounting", "agency", "staff"] },
    { path: "/pending-tasks", icon: AlertCircle, label: "Pending Tasks", roles: ["admin"] }, // Admin only
    { path: "/planning", icon: Calendar, label: user?.role === "staff" ? "My Flights" : "Global Planning", roles: ["admin", "operations", "accounting", "agency", "staff"] },
    { path: "/flights", icon: Plane, label: "Flights", roles: ["admin", "operations", "accounting", "agency", "staff"] },
    { path: "/agents", icon: DollarSign, label: "My Costs", roles: ["staff"] }, // Staff only - view their own costs
    { path: "/agencies", icon: Building2, label: "Agencies", roles: ["admin", "operations", "accounting", "agency"], permission: "agencies" },
    { path: "/reports", icon: FileText, label: "Reports", roles: ["admin", "operations", "agency", "staff"], permission: "reports" },
  ];
  
  // Accounting submenu items
  const accountingItems = [
    { path: "/invoices", icon: Receipt, label: "Invoices", roles: ["admin", "accounting"] },
    { path: "/pay-invoice", icon: Receipt, label: "Pay Invoice", roles: ["admin", "accounting", "agency"] },
    { 
      path: "/agency-statements", 
      icon: FileText, 
      label: user?.role === 'agency' ? "My Invoices" : "Agency Statements", 
      roles: ["admin", "accounting", "agency"] 
    },
    { path: "/statistics", icon: BarChart3, label: "Statistics", roles: ["admin", "accounting", "agency"] },
    { path: "/services", icon: Briefcase, label: "Services", roles: ["admin", "operations"] },
  ];
  
  // ATT Settings submenu items (Company-related settings) - Admin only
  const attSettingsItems = [
    { path: "/settings", icon: Settings, label: "Company Settings", roles: ["admin"] },
    { path: "/agents", icon: Users, label: "Agents on Duty", roles: ["admin"] },
    { path: "/admin-users", icon: Users, label: "User Management", roles: ["admin"] },
    { path: "/permissions", icon: Shield, label: "Permissions", roles: ["admin"] },
  ];
  
  // Email and Notification submenu items
  const emailNotificationItems = [
    { path: "/email-automation", icon: Mail, label: "Email Automation", roles: ["admin"] },
    { path: "/email-templates", icon: Mail, label: "Email Templates", roles: ["admin"] },
    { path: "/email-logs", icon: Mail, label: "Email Logs", roles: ["admin"] },
    { path: "/popups-notices", icon: MessageSquare, label: "Popups and Notices", roles: ["admin"] },
  ];
  
  // APP Settings submenu items (Application configuration)
  const appSettingsItems = [
    { path: "/backups", icon: Database, label: "Backups", roles: ["admin"] },
    { path: "/airlines-data", icon: PlaneTakeoff, label: "Airlines Data", roles: ["admin"] },
    { path: "/airports", icon: Plane, label: "Airports", roles: ["admin"] },
    { path: "/banned-domains", icon: Shield, label: "Banned Domains", roles: ["admin"] },
    { path: "/agency-users", icon: Users, label: "Users", roles: ["agency"] },
    { path: "/user-manual", icon: BookOpen, label: "User Manual", roles: ["admin", "operations", "accounting", "agency", "staff"] },
    { path: "/login-history", icon: History, label: "Login History", roles: ["admin", "agency"] },
  ];

  // Filter menu items based on user role AND permissions
  const navItems = allNavItems.filter(item => {
    // Check if user's role is allowed
    if (!item.roles.includes(user?.role || "operations")) return false;
    
    // If item requires specific permission, check it
    if (item.permission) {
      return hasPermission(item.permission);
    }
    
    // No specific permission required, allow access
    return true;
  });
  
  const filteredAccountingItems = accountingItems.filter(item =>
    item.roles.includes(user?.role || "operations")
  );
  
  const filteredAttSettingsItems = attSettingsItems.filter(item =>
    item.roles.includes(user?.role || "operations")
  );
  
  const filteredAppSettingsItems = appSettingsItems.filter(item =>
    item.roles.includes(user?.role || "operations")
  );
  
  const filteredEmailNotificationItems = emailNotificationItems.filter(item =>
    item.roles.includes(user?.role || "operations")
  );
  
  const hasAccountingAccess = filteredAccountingItems.length > 0;
  const hasAttSettingsAccess = filteredAttSettingsItems.length > 0;
  const hasAppSettingsAccess = filteredAppSettingsItems.length > 0;
  const hasEmailNotificationAccess = filteredEmailNotificationItems.length > 0;

  return (
    <div className="min-h-screen" style={{ background: '#0A1128' }}>
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 flex flex-col" style={{ background: 'rgba(10, 17, 40, 0.9)', borderRight: '1px solid rgba(74, 90, 138, 0.3)' }}>
        <div className="p-4 pb-3">
          <div className="flex items-center justify-center w-14 h-14 rounded-full mb-2 p-2 mx-auto" style={{ background: 'white' }}>
            <img 
              src={logoUrl} 
              alt="Air Transport Trade Logo" 
              className="w-full h-full object-contain"
              onError={(e) => { e.target.src = "https://customer-assets.emergentagent.com/job_rolebased-invoice/artifacts/q1mu9arn_chat_edit_image_20251029_170030.png"; }}
            />
          </div>
          <h2 className="text-base font-bold mb-0.5 text-center" style={{ color: '#FFFFFF', letterSpacing: '0.5px' }}>AIR TRANSPORT TRADE</h2>
          <p className="text-xs text-center" style={{ color: '#B0B0B0' }}>VIP Service Management</p>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 pb-4 space-y-1 custom-scrollbar" style={{ 
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(74, 90, 138, 0.5) transparent'
        }}>
        <style jsx>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(74, 90, 138, 0.5);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(74, 90, 138, 0.7);
          }
        `}</style>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-9 text-sm"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  style={{
                    background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    color: isActive ? '#FFFFFF' : '#B0B0B0',
                    borderLeft: isActive ? '3px solid #FFFFFF' : 'none',
                    transition: 'all 0.2s ease',
                    paddingLeft: '12px'
                  }}
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
          
          {/* Accounting Section */}
          {hasAccountingAccess && (
            <div className="mt-1">
              <Button
                variant="ghost"
                className="w-full justify-start h-9 text-sm"
                onClick={toggleAccountingSection}
                style={{
                  background: (location.pathname === '/invoices' || location.pathname === '/agency-statements' || location.pathname === '/statistics' || location.pathname === '/services') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  color: (location.pathname === '/invoices' || location.pathname === '/agency-statements' || location.pathname === '/statistics' || location.pathname === '/services') ? '#FFFFFF' : '#B0B0B0',
                  transition: 'all 0.2s ease',
                  paddingLeft: '12px'
                }}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                Accounting
                <ChevronRight 
                  className="ml-auto h-4 w-4" 
                  style={{ 
                    transform: accountingOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </Button>
              <div style={{
                maxHeight: accountingOpen ? '500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-in-out'
              }}>
                <div className="ml-6 mt-1 space-y-0.5">
                  {filteredAccountingItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-8 text-sm"
                          style={{
                            background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            color: isActive ? '#FFFFFF' : '#B0B0B0',
                            borderLeft: isActive ? '3px solid #FFFFFF' : 'none',
                            transition: 'all 0.2s ease',
                            paddingLeft: '8px'
                          }}
                        >
                          <Icon className="mr-2 h-3.5 w-3.5" />
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* ATT Settings Section */}
          {hasAttSettingsAccess && (
            <div className="mt-1">
              <Button
                variant="ghost"
                className="w-full justify-start h-9 text-sm"
                onClick={toggleAttSettingsSection}
                style={{
                  background: (location.pathname === '/settings' || location.pathname === '/agents' || location.pathname === '/admin-users' || location.pathname === '/permissions') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  color: (location.pathname === '/settings' || location.pathname === '/agents' || location.pathname === '/admin-users' || location.pathname === '/permissions') ? '#FFFFFF' : '#B0B0B0',
                  transition: 'all 0.2s ease',
                  paddingLeft: '12px'
                }}
              >
                <Settings className="mr-2 h-4 w-4" />
                ATT Settings
                <ChevronRight 
                  className="ml-auto h-4 w-4" 
                  style={{ 
                    transform: attSettingsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </Button>
              <div style={{
                maxHeight: attSettingsOpen ? '500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-in-out'
              }}>
                <div className="ml-6 mt-1 space-y-0.5">
                  {filteredAttSettingsItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-8 text-sm"
                          style={{
                            background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            color: isActive ? '#FFFFFF' : '#B0B0B0',
                            borderLeft: isActive ? '3px solid #FFFFFF' : 'none',
                            transition: 'all 0.2s ease',
                            paddingLeft: '8px'
                          }}
                        >
                          <Icon className="mr-2 h-3.5 w-3.5" />
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* Email and Notification Section */}
          {hasEmailNotificationAccess && (
            <div className="mt-1">
              <Button
                variant="ghost"
                className="w-full justify-start h-9 text-sm"
                onClick={toggleEmailNotificationSection}
                style={{
                  background: (location.pathname === '/email-automation' || location.pathname === '/email-templates' || location.pathname === '/popups-notices' || location.pathname === '/email-logs') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  color: (location.pathname === '/email-automation' || location.pathname === '/email-templates' || location.pathname === '/popups-notices' || location.pathname === '/email-logs') ? '#FFFFFF' : '#B0B0B0',
                  transition: 'all 0.2s ease',
                  paddingLeft: '12px'
                }}
              >
                <Bell className="mr-2 h-4 w-4" />
                Email and Notification
                <ChevronRight 
                  className="ml-auto h-4 w-4" 
                  style={{ 
                    transform: emailNotificationOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </Button>
              <div style={{
                maxHeight: emailNotificationOpen ? '500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-in-out'
              }}>
                <div className="ml-6 mt-1 space-y-0.5">
                  {filteredEmailNotificationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-8 text-sm"
                          style={{
                            background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            color: isActive ? '#FFFFFF' : '#B0B0B0',
                            borderLeft: isActive ? '3px solid #FFFFFF' : 'none',
                            transition: 'all 0.2s ease',
                            paddingLeft: '8px'
                          }}
                        >
                          <Icon className="mr-2 h-3.5 w-3.5" />
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          
          {/* APP Settings Section */}
          {hasAppSettingsAccess && (
            <div className="mt-1">
              <Button
                variant="ghost"
                className="w-full justify-start h-9 text-sm"
                onClick={toggleAppSettingsSection}
                style={{
                  background: (location.pathname === '/backups' || location.pathname === '/airlines-data' || location.pathname === '/airports' || location.pathname === '/user-manual' || location.pathname === '/login-history') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  color: (location.pathname === '/backups' || location.pathname === '/airlines-data' || location.pathname === '/airports' || location.pathname === '/user-manual' || location.pathname === '/login-history') ? '#FFFFFF' : '#B0B0B0',
                  transition: 'all 0.2s ease',
                  paddingLeft: '12px'
                }}
              >
                <Settings className="mr-2 h-4 w-4" />
                APP Settings
                <ChevronRight 
                  className="ml-auto h-4 w-4" 
                  style={{ 
                    transform: appSettingsOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }} 
                />
              </Button>
              <div style={{
                maxHeight: appSettingsOpen ? '500px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.3s ease-in-out'
              }}>
                <div className="ml-6 mt-1 space-y-0.5">
                  {filteredAppSettingsItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <Link key={item.path} to={item.path}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start h-8 text-sm"
                          style={{
                            background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                            color: isActive ? '#FFFFFF' : '#B0B0B0',
                            borderLeft: isActive ? '3px solid #FFFFFF' : 'none',
                            transition: 'all 0.2s ease',
                            paddingLeft: '8px'
                          }}
                        >
                          <Icon className="mr-2 h-3.5 w-3.5" />
                          {item.label}
                        </Button>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="p-3 rounded-lg mb-3" style={{ background: 'rgba(30, 42, 74, 0.6)', border: '1px solid rgba(74, 90, 138, 0.3)' }}>
            <p className="text-sm font-medium" style={{ color: '#E8E8E8' }}>{user?.full_name}</p>
            <p className="text-xs" style={{ color: '#B0B0B0' }}>{user?.role}</p>
            {user?.role === 'agency' && agencyInfo && (
              <div className="mt-2 pt-2 border-t" style={{ borderColor: 'rgba(74, 90, 138, 0.3)' }}>
                <p className="text-xs font-medium" style={{ color: '#A855F7' }}>{agencyInfo.name}</p>
                {agencyInfo.address && (
                  <p className="text-xs mt-1" style={{ color: '#B0B0B0' }}>{agencyInfo.address}</p>
                )}
              </div>
            )}
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={onLogout}
            data-testid="logout-button"
            style={{ borderColor: '#FFFFFF', color: '#FFFFFF', background: 'transparent' }}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64">
        {/* Copyright Header - Sticky */}
        <div className="sticky top-0 z-50 px-8 py-2 border-b" style={{ background: 'rgba(30, 42, 74, 0.98)', borderColor: 'rgba(74, 90, 138, 0.3)' }}>
          <p className="text-xs text-center" style={{ color: '#B0B0B0' }}>
            © 2025 VIP Service Application. Developed by the VIP Handling IT Team. All rights reserved.
          </p>
        </div>

        {/* Top Header with User Info */}
        <div className="px-8 py-4 border-b" style={{ background: 'rgba(30, 42, 74, 0.6)', borderColor: 'rgba(74, 90, 138, 0.3)' }}>
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold" style={{ color: '#FFD700' }}>
                {location.pathname === '/' && 'Dashboard'}
                {location.pathname === '/planning' && 'Global Planning'}
                {location.pathname === '/flights' && 'Flight Management'}
                {location.pathname === '/agencies' && 'Agencies'}
                {location.pathname === '/reports' && 'Operational Reports'}
                {location.pathname === '/invoices' && 'Invoices'}
                {location.pathname === '/pay-invoice' && 'Pay Invoice'}
                {location.pathname === '/agency-statements' && 'Agency Statements'}
                {location.pathname === '/statistics' && 'Statistics & Analytics'}
                {location.pathname === '/services' && 'Services'}
                {location.pathname === '/airports' && 'Airports'}
                {location.pathname === '/agents' && 'Agents'}
                {location.pathname === '/settings' && 'Company Settings'}
                {location.pathname === '/admin-users' && 'Admin Users'}
                {location.pathname === '/permissions' && 'User Permissions'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              {user?.role === 'agency' && user?.agency_id && (
                <div className="text-right pr-4 border-r" style={{ borderColor: 'rgba(74, 90, 138, 0.4)' }}>
                  <p className="text-sm font-semibold" style={{ color: '#FFD700' }}>
                    Agency
                  </p>
                  <p className="text-xs" style={{ color: '#E8E8E8' }}>
                    {/* Agency name will be fetched */}
                    Logged In
                  </p>
                </div>
              )}
              <div className="text-right">
                <p className="text-sm font-semibold" style={{ color: '#E8E8E8' }}>
                  {user?.full_name}
                </p>
                <p className="text-xs capitalize" style={{ color: '#B0B0B0' }}>
                  {user?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Page Content */}
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
